import { Result, handleServerErrors, Events, CustomError, ServerError } from 'core/infrastructure';
import {
	TicketDTO,
	SaleDTO,
	PaymentDTO,
	BuyerCustomerDTO,
	Res,
	InvoiceParamsFromPosDTO,
	TicketLocationDTO
} from 'core/api';
import { OrderingFacade, PrintFacade, SalesOrderFacade, DeliveryNoteFacade } from 'pos/facades';


export async function addPayments(orderId: number, payments: PaymentDTO[], prepayAmount: number): Promise<Result<SaleDTO>> {
	return await run(async () => {
		const sale = await SalesOrderFacade.addPayments(orderId, payments, prepayAmount);
		await Events.raise('pos:sales-order:changed');
		return sale;
	});
}

export async function changeCustomer(salesOrderId: number, customer: BuyerCustomerDTO): Promise<void> {
	await run(async () => {
		await SalesOrderFacade.changeCustomer(salesOrderId, customer);
		await Events.raise('pos:sales-order:changed');
	});
}

export async function print(salesOrderId: number, params: PrintFacade.PrintParams): Promise<void> {
	await run(async () => await PrintFacade.printSalesOrderOnPrinter(salesOrderId, params));
}

export async function createInvoice(orderId: number, params: InvoiceParamsFromPosDTO): Promise<Result<SaleDTO, CustomError>> {
	return await run(async () => {
		const sale = await SalesOrderFacade.createInvoice(orderId, params);
		await Events.raise('pos:sales-order:changed');
		return sale;
	});
}

export async function createRefund(orderId: number, params: InvoiceParamsFromPosDTO): Promise<Result<SaleDTO, CustomError>> {
	return await run(async () => {
		const sale = await SalesOrderFacade.createRefund(orderId, params);
		await Events.raise('pos:sales-order:changed');
		return sale;
	});
}

export async function cancel(orderId: number): Promise<void> {
	await run(async () => {
		await SalesOrderFacade.cancel(orderId);
		await Events.raise('pos:sales-order:changed');
	});
}

export async function reopen(orderId: number, location: TicketLocationDTO): Promise<Result<TicketDTO, CustomError>> {
	return await run(async () => SalesOrderFacade.reopen(orderId, location));
}

export async function reopenInTicket(orderId: number, location: TicketLocationDTO, targetTicketId: number): Promise<Result<TicketDTO, CustomError>> {
	return await run(async () => SalesOrderFacade.reopenInTicket(orderId, location, targetTicketId));
}

export async function prepare(ticketIds: number[]): Promise<void> {
	await run(async () => await OrderingFacade.prepareTickets(ticketIds, false));
}

export async function prepareAgain(ticketIds: number[]): Promise<void> {
	await run(async () => await OrderingFacade.prepareAgain(ticketIds));
}

export async function setTableCompanions(orderId: number, tableCompanions: Nullable<number>) {
	await run(async () => {
		await SalesOrderFacade.setTableCompanions(orderId, tableCompanions);
		await Events.raise('pos:sales-order:changed');
	});
}

export async function convertToDeliveryNote(SalesOrderIds: number[]): Promise<Result<SaleDTO>> {
	return await run(async () => {
		return await DeliveryNoteFacade.convertToDeliveryNote(SalesOrderIds);
	});
}

async function run<T>(fn: () => Promise<T>): Promise<Result<T, CustomError>> {
	const handledError: CustomError = new ServerError('ERROR NO CAPTURADO');

	const result = await handleServerErrors(fn, {
		onDomainError: async (error) => {
			await Events.raise('pos:sales-order:operation-failed', { msg: error.message, error: error });
		},

		onPrintError: async (error) => {
			await Events.raise('pos:sales-order:operation-failed', { msg: Res.Msg.MSG_TICKET_COULD_NOT_BE_PRINTED(error.message), error: error });
		},

		onInvalidPaymentError: async (error) => {
			await Events.raise('pos:sales-order:operation-failed', { msg: error.message, error: error });
		},

		onFrozenSalesOrderError: async (error) => {
			await Events.raise('pos:sales-order:operation-failed', { msg: Res.Msg.MSG_SALES_ORDER_HAS_BEEN_CLOSED_BY_ANOTHER_USER(), error: error });
		},

		onInvalidCustomerCifError: async (error) => {
			await Events.raise('pos:sales-order:operation-failed', { msg: Res.Msg.MSG_INVALID_CUSTOMER_CIF_TO_CREATE_STANDARD_INVOICE(), error: error });
		},

		onFrozenPaymentsError: async (error) => {
			await Events.raise('pos:sales-order:operation-failed', { msg: Res.Msg.MSG_SALES_ORDER_PAYMENTS_CANNOT_BE_MODIFIED_BECAUSE_IT_WAS_CLOSED_BY_ANOTHER_USER(), error: error });
		},

		onTicketNotFoundError: async (error) => {
			await Events.raise('pos:sales-order:operation-failed', { msg: Res.Msg.MSG_TICKET_HAS_BEEN_DELETED_OR_CLOSED_BY_ANOTHER_USER(), error: error });
		}
	});

	return result.ok ? Result.ok(result.value) : Result.error(handledError);
}

